import ButtonBase from '@material-ui/core/ButtonBase';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { useLocation } from 'react-router-dom';
import { isAuthenticatedState } from '../../../api/appState';
import { ProductDetailProduct } from '../../../api/products/productDetail';
import { showProductDetailSpagettyNavigation } from '../../../config';
import ProductDetailSpagettyLongDescription from './ProductDetailSpagettyLongDescription';
import ProductDetailSpagettyParameters from './ProductDetailSpagettyParameters';
import ProductDetailSpagettyDocuments from './ProductDetailSpagettyDocuments';

export interface ProductDetailSpagettyProps extends StandardDivProps<ProductDetailSpagettyClassKey> {
  product: ProductDetailProduct;
}

export type ProductDetailSpagettyClassKey = 'root';

const themeSettings = { name: 'ProductDetailSpagetty' };
const useStyles = makeStyles(
  (/* theme */) => ({
    root: {},
  }),
  themeSettings,
);

const ProductDetailSpagetty = React.forwardRef<HTMLDivElement, ProductDetailSpagettyProps>(
  ({ className, classes: pClasses, product }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const isAuthenticated = useRecoilValue(isAuthenticatedState);

    const innerRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(ref, () => innerRef.current as any);

    // type ButtonCode = 'parameters' | 'longDescription';
    // const allButtons: SpagettyNavigationButton<ButtonCode>[] = [
    //   { code: 'longDescription', label: 'Podrobnosti', urlHash: '#poodrobnosti' },
    //   { code: 'parameters', label: 'Parametry', urlHash: '#parametry' },
    // ];
    const allButtons = {
      longDescription: { label: 'Podrobnosti', urlHash: '#podrobnosti' },
      parameters: { label: 'Parametry', urlHash: '#parametry' },
      documents: { label: 'Documenty', urlHash: '#dokumenty' },
    };
    type ButtonCode = keyof typeof allButtons;

    const [visibleButtons, setVisibleButtons] = React.useState<ButtonCode[]>(['parameters']);
    const buttons = Object.entries(allButtons)
      .filter(([code]) => visibleButtons.includes(code as ButtonCode))
      .map(([code, b]) => ({ code, ...b }));

    const handleToggleButton = React.useCallback(
      (code: ButtonCode) => (state?: boolean) => {
        setVisibleButtons((prev) => {
          const idx = prev.findIndex((bc) => bc === code);

          if (state === undefined) {
            if (idx !== -1) {
              return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
            }
            return [...prev, code];
          }

          if (state && idx === -1) return [...prev, code];
          if (!state && idx !== -1) return [...prev.slice(0, idx), ...prev.slice(idx + 1)];

          return prev;
        });
      },
      [],
    );

    return (
      <>
        <div ref={innerRef} className={clsx(classes.root, className)}>
          {showProductDetailSpagettyNavigation && <SpagettyNavigation fixed={false} buttons={buttons} />}
          <ProductDetailSpagettyParameters
            anchor={allButtons.parameters.urlHash}
            product={product}
            onNaviButtonShow={handleToggleButton('parameters')}
          />
          {isAuthenticated && (
            <ProductDetailSpagettyDocuments anchor={allButtons.documents.urlHash} product={product} />
          )}
          <ProductDetailSpagettyLongDescription
            anchor={allButtons.longDescription.urlHash}
            productId={product.id}
            onNaviButtonShow={handleToggleButton('longDescription')}
          />
        </div>
      </>
    );
  },
);

ProductDetailSpagetty.displayName = 'ProductDetailSpagetty';

export default withThemeProps(themeSettings)(ProductDetailSpagetty);

interface SpagettyNavigationProps {
  buttons: SpagettyNavigationButton<any>[];
  classes: Record<SpagettyNavigationClassKey, string>;
  fixed: boolean;
}

type SpagettyNavigationClassKey = 'root' | 'fixed' | 'button' | 'buttonActive';

const SpaggetyNavigationWithRef = React.forwardRef<any, SpagettyNavigationProps>(({ buttons, classes, fixed }, ref) => {
  const location = useLocation();

  const activeBtn = location && location.hash ? location.hash : buttons[0]?.urlHash;

  return (
    <div ref={ref} className={clsx(classes.root, { [classes.fixed]: fixed })}>
      {buttons.map((btn, idx) => (
        <ButtonBase
          key={idx.toString()}
          component={'a'}
          className={clsx(classes.button, { [classes.buttonActive]: activeBtn === btn.urlHash })}
          href={btn.urlHash}
        >
          {btn.label}
        </ButtonBase>
      ))}
    </div>
  );
});
SpaggetyNavigationWithRef.displayName = 'SpaggetyNavigationWithRef';

const SpagettyNavigation = withStyles((theme: Theme) =>
  createStyles<SpagettyNavigationClassKey, {}>({
    root: {},
    fixed: {
      backgroundColor: theme.palette.background.default,
      boxShadow: theme.shadows[8],
    },
    button: {
      ...theme.typography.body1,
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.text.primary,
      height: theme.spacing(8),
      marginRight: theme.spacing(4),
    },
    buttonActive: {
      color: theme.palette.primary.main,
      borderBottom: `3px solid ${theme.palette.primary.main}`,
    },
  }),
)(SpaggetyNavigationWithRef);

interface SpagettyNavigationButton<Code extends string> {
  code: Code;
  label: string;
  urlHash: string;
}
